import { Check, ChevronDown, Search } from 'lucide-react';
import type React from 'react';
import { forwardRef, type ButtonHTMLAttributes, type KeyboardEvent, type ReactNode, useEffect, useId, useMemo, useRef, useState } from 'react';
import { cn } from '../../lib/cn';
import { matchesSearch } from '../../lib/format';
import type { Placement, Rect } from './layer';
import { Popover } from './Popover';

export type Option = {
  value: string;
  label: string;
  description?: string;
  icon?: ReactNode;
  keywords?: string;
  disabled?: boolean;
  group?: string;
};

type SearchListProps = {
  options: Option[];
  value: string | string[] | null;
  onSelect: (value: string) => void;
  /** Закрити після вибору (для спливного вікна). */
  onDone?: () => void;
  multiple?: boolean;
  label: string;
  placeholder?: string;
  emptyText?: string;
  footer?: ReactNode;
  header?: ReactNode;
  className?: string;
  loading?: boolean;
  inputRef?: React.RefObject<HTMLInputElement | null>;
  autoFocus?: boolean;
  listClassName?: string;
  /** Пошук на сервері: локально не фільтрувати, лише повідомляти запит. */
  onQueryChange?: (q: string) => void;
  remoteFilter?: boolean;
};

/**
 * Список із пошуком (патерн «комбінований список»): поле з role="combobox",
 * перелік role="listbox", стрілки, Enter. Використовується у спливному вікні й у діалогах.
 */
export function SearchList({
  options,
  value,
  onSelect,
  onDone,
  multiple,
  label,
  placeholder = 'Пошук…',
  emptyText = 'Нічого не знайдено',
  footer,
  header,
  className,
  loading,
  inputRef: externalRef,
  autoFocus,
  listClassName,
  onQueryChange,
  remoteFilter,
}: SearchListProps) {
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const listId = useId();
  const ownRef = useRef<HTMLInputElement>(null);
  const inputRef = externalRef ?? ownRef;
  const listRef = useRef<HTMLUListElement>(null);

  const filtered = useMemo(
    () => (remoteFilter ? options : options.filter((o) => matchesSearch(`${o.label} ${o.description ?? ''} ${o.keywords ?? ''}`, query))),
    [options, query, remoteFilter],
  );

  useEffect(() => {
    if (active >= filtered.length) setActive(Math.max(0, filtered.length - 1));
  }, [filtered.length, active]);

  useEffect(() => {
    listRef.current?.querySelector(`[data-index="${active}"]`)?.scrollIntoView({ block: 'nearest' });
  }, [active]);

  useEffect(() => {
    if (autoFocus) inputRef.current?.focus({ preventScroll: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selected = (v: string) => (Array.isArray(value) ? value.includes(v) : value === v);

  const choose = (o: Option | undefined) => {
    if (!o || o.disabled) return;
    onSelect(o.value);
    if (!multiple) onDone?.();
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((a) => Math.min(filtered.length - 1, a + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((a) => Math.max(0, a - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      choose(filtered[active]);
    } else if (e.key === 'Tab' && onDone) {
      onDone();
    }
  };

  let lastGroup: string | undefined;
  return (
    <div className={cn('flex min-h-0 flex-col', className)}>
      {header}
      <div className="flex items-center gap-2 border-b border-line px-2.5">
        <Search className="size-4 shrink-0 text-fg-3" aria-hidden />
        <input
          ref={inputRef}
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setActive(0);
            onQueryChange?.(e.target.value);
          }}
          onKeyDown={onKeyDown}
          role="combobox"
          aria-expanded="true"
          aria-controls={listId}
          aria-autocomplete="list"
          aria-activedescendant={filtered[active] ? `${listId}-${active}` : undefined}
          aria-label={label}
          placeholder={placeholder}
          className="h-9 w-full bg-transparent text-body text-fg outline-none placeholder:text-fg-3"
        />
      </div>
      <ul
        ref={listRef}
        id={listId}
        role="listbox"
        aria-label={label}
        aria-multiselectable={multiple || undefined}
        className={cn('max-h-[300px] overflow-y-auto overscroll-contain p-1', listClassName)}
      >
        {loading && <li className="px-2.5 py-2 text-meta text-fg-3">Завантаження…</li>}
        {!loading && filtered.length === 0 && <li className="px-2.5 py-2 text-meta text-fg-3">{emptyText}</li>}
        {filtered.map((o, i) => {
          const showGroup = o.group && o.group !== lastGroup;
          lastGroup = o.group;
          return (
            <li key={o.value} role="presentation">
              {showGroup && <div className="px-2.5 pt-2 pb-1 text-caption font-medium text-fg-3">{o.group}</div>}
              <div
                id={`${listId}-${i}`}
                data-index={i}
                role="option"
                aria-selected={selected(o.value)}
                aria-disabled={o.disabled || undefined}
                onPointerMove={() => setActive(i)}
                onPointerDown={(e) => e.preventDefault()}
                onClick={() => choose(o)}
                className={cn(
                  'flex min-h-8 cursor-pointer items-center gap-2.5 rounded-sm px-2.5 py-1 text-body',
                  i === active && 'bg-surface-2',
                  o.disabled && 'cursor-not-allowed opacity-50',
                )}
              >
                {o.icon && <span className="flex shrink-0 items-center">{o.icon}</span>}
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-fg">{o.label}</span>
                  {o.description && <span className="block truncate text-caption text-fg-3">{o.description}</span>}
                </span>
                {selected(o.value) && <Check className="size-4 shrink-0 text-accent-fg" aria-hidden />}
              </div>
            </li>
          );
        })}
      </ul>
      {footer && <div className="border-t border-line p-1">{footer}</div>}
    </div>
  );
}

type SearchSelectProps = Omit<SearchListProps, 'onDone' | 'inputRef' | 'autoFocus'> & {
  open: boolean;
  onClose: () => void;
  anchor: HTMLElement | Rect | null;
  placement?: Placement;
};

/** Список із пошуком у спливному вікні. */
export function SearchSelect({ open, onClose, anchor, placement = 'bottom-start', className, ...rest }: SearchSelectProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  return (
    <Popover
      open={open}
      onClose={onClose}
      anchor={anchor}
      placement={placement}
      initialFocus={inputRef}
      role="presentation"
      className={cn('flex w-[300px] max-w-[calc(100vw-16px)] flex-col overflow-hidden', className)}
    >
      {open && <SearchList {...rest} inputRef={inputRef} onDone={onClose} />}
    </Popover>
  );
}

type SelectButtonProps = Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'value'> & {
  placeholder?: string;
  display?: ReactNode;
  icon?: ReactNode;
  invalid?: boolean;
  size?: 'sm' | 'md';
  variant?: 'field' | 'ghost';
};

/** Кнопка-тригер у вигляді поля: показує обране значення й стрілку. */
export const SelectButton = forwardRef<HTMLButtonElement, SelectButtonProps>(function SelectButton(
  { placeholder = 'Не обрано', display, icon, invalid, className, size = 'md', variant = 'field', type = 'button', ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      role="combobox"
      aria-haspopup="listbox"
      aria-expanded={rest['aria-expanded'] ?? false}
      aria-invalid={invalid || rest['aria-invalid'] || undefined}
      className={cn(
        'inline-flex min-w-0 items-center gap-2 rounded-sm text-left text-body transition-colors',
        size === 'md' ? 'h-[var(--control-md)] px-2.5' : 'h-[var(--control-sm)] px-2 text-meta',
        variant === 'field'
          ? 'w-full border border-line-input bg-surface hover:border-fg-3 aria-[invalid=true]:border-danger'
          : 'border border-transparent hover:bg-surface-2',
        'disabled:cursor-not-allowed disabled:opacity-60',
        className,
      )}
      {...rest}
    >
      {icon}
      <span className={cn('min-w-0 flex-1 truncate', !display && 'text-fg-3')}>{display ?? placeholder}</span>
      {variant === 'field' && <ChevronDown className="size-3.5 shrink-0 text-fg-3" aria-hidden />}
    </button>
  );
});
